import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { getClips, getClipSignedUrl, recordListen, incrementMinutesListened } from '../services/HalogenService';
import { usePostHog } from 'posthog-js/react'

const AudioContext = createContext();

export const useAudio = () => useContext(AudioContext);

// Wrap the entire app in AudioProvider (which we've done in index.js) to enable audio playback
// To access the audio context in a component, use the useAudio hook as follows:
// const { playClip, playNextClip, pauseClip, resumeClip, addToQueue, removeFromQueue, moveClipInQueue, setVolume, isPlaying, currentlyPlayingClip, queue } = useAudio();
export const AudioProvider = ({ children }) => {
    const posthog = usePostHog();
    // isPlaying state and refs are used to keep track of the audio player's state
    const [isPlaying, setIsPlaying] = useState(false);
    const isPlayingRef = useRef(isPlaying);
    useEffect(() => {
        isPlayingRef.current = isPlaying;
    }, [isPlaying]);
    // currentlyPlayingClip state and ref are used to keep track of the currently playing clip
    const [currentlyPlayingClip, setCurrentlyPlayingClip] = useState(null);
    const currentlyPlayingClipRef = useRef(currentlyPlayingClip);
    useEffect(() => {
        currentlyPlayingClipRef.current = currentlyPlayingClip;
    }, [currentlyPlayingClip]);
    // queue/autoqueue state and ref are used to keep track of the queue of clips to play
    const [queue, setQueue] = useState([]);
    const queueRef = useRef(queue);
    useEffect(() => {
        queueRef.current = [...queue];
    }, [queue]);
    const [autoqueue, setAutoqueue] = useState([]);
    const autoqueueRef = useRef(autoqueue);
    useEffect(() => {
        autoqueueRef.current = [...autoqueue];
    }, [autoqueue]);
    const [autoqueueIndex, setAutoqueueIndex] = useState(0);
    // playbackRate state is used to keep track of the audio player's playback rate
    const [playbackRate, setPlaybackRateState] = useState(1.25); // Default playback rate is 1.25
    const playbackRateRef = useRef(playbackRate);
    useEffect(() => {
        playbackRateRef.current = playbackRate;
    }, [playbackRate]);
    // state variables to track time listened
    const [sessionPlayedSeconds, setSessionPlayedSeconds] = useState(0);
    const sessionPlayedSecondsRef = useRef(sessionPlayedSeconds);
    useEffect(() => {
        sessionPlayedSecondsRef.current = sessionPlayedSeconds;
    }, [sessionPlayedSeconds]);
    const lastMinuteSentRef = useRef(-1);
    const [minutesListenedId, setMinutesListenedId] = useState(null);
    // audioRef is used to interact with the audio player
    const audioRef = useRef(new Audio());

    // Add an event listener to the audio player to play the next clip in the queue when the current clip ends
    useEffect(() => {
        const playNextClipAutomatically = () => {
            if (queueRef.current.length > 0) {
                playClip(queueRef.current[0]);
                const updatedQueue = queueRef.current.slice(1);
                setQueue(updatedQueue);
            } else if (autoqueueRef.current.length > 0) {
                playClip(autoqueueRef.current[0]);
                const updatedAutoqueue = autoqueueRef.current.slice(1);
                setAutoqueue(updatedAutoqueue);
            }
        };

        audioRef.current.addEventListener('ended', playNextClipAutomatically);

        return () => {
            audioRef.current.removeEventListener('ended', playNextClipAutomatically);
        };
    }, []);

    // Update the played time every second
    useEffect(() => {
        let lastReportedTime = 0; // Last time reported to the sessionPlayedSeconds
        const updatePlayedTime = () => {
            const currentTime = audioRef.current.currentTime;
            // Determine the difference from the last reported time
            const timeDifference = currentTime - lastReportedTime;

            // Only update if the time difference suggests normal playback
            if (timeDifference > 0 && timeDifference <= 5) {
                const newSessionPlayedSeconds = sessionPlayedSecondsRef.current + timeDifference;
                setSessionPlayedSeconds(newSessionPlayedSeconds);
                const playedMinutes = Math.floor((newSessionPlayedSeconds + 30) / 60);
                if (playedMinutes > lastMinuteSentRef.current) {
                    posthog.capture('minutes_listened', {
                        total_minutes: playedMinutes
                    });
                    if (playedMinutes > 0) {
                        incrementMinutesListened();
                    }
                    lastMinuteSentRef.current = playedMinutes;
                }
            }
            lastReportedTime = currentTime;
        };

        const intervalId = setInterval(updatePlayedTime, 1000); // Update every second

        return () => {
            clearInterval(intervalId);
        };
    }, []);


    // Fetch more clips for the autoqueue when it's running low
    useEffect(() => {
        const fetchClips = async () => {
            if (autoqueue.length < 5) {
                const newClips = await getClips(100, 'hot', autoqueueIndex);
                setAutoqueue(autoqueue.concat(newClips));
                setAutoqueueIndex(autoqueueIndex + 1);
            }
        };

        fetchClips();
    }, [autoqueue, autoqueueIndex]);

    const playClip = async (clip) => {
        try {
            removeFromQueueById(clip.id);
            removeFromAutoqueueById(clip.id);
            const response = await getClipSignedUrl(clip.id);
            posthog.capture('clip_started', {
                clip: clip.id,
                clip_title: clip.title,
                playbackRate: playbackRateRef.current
            })
            const audioUrl = response.signedUrl;
            audioRef.current.src = audioUrl;
            audioRef.current.load();
            setPlaybackRate(playbackRateRef.current); // Set the playback rate
            await audioRef.current.play();
            setIsPlaying(true);
            setCurrentlyPlayingClip(clip);
            recordListen(clip.id);
        } catch (error) {
            console.error('Error playing clip:', error);
            setIsPlaying(false);
        }
    };

    const playNextClip = async () => {
        const nextClip = queueRef.current[0]; // Access the next clip directly
        // Check if there's a next clip to play and play it
        if (nextClip) {
            const updatedQueue = queueRef.current.slice(1);
            setQueue(updatedQueue);
            posthog.capture('played_next_clip_in_queue', {
                prev_clip: audioRef?.current.id,
                position: audioRef?.current.currentTime,
                next_clip: nextClip.id,
            })
            queueRef.current = updatedQueue;
            try {
                await playClip(nextClip);
            } catch (error) {
                console.error("Error playing next clip:", error);
            }
        } else {
            const nextAutoqueueClip = autoqueueRef.current[0];
            if (nextAutoqueueClip) {
                const updatedAutoqueue = autoqueueRef.current.slice(1);
                setAutoqueue(updatedAutoqueue);
                posthog.capture('played_next_clip_in_autoqueue', {
                    prev_clip: audioRef?.current.id,
                    position: audioRef?.current.currentTime,
                    next_clip: nextAutoqueueClip.id,
                })
                autoqueueRef.current = updatedAutoqueue;
                try {
                    await playClip(nextAutoqueueClip);
                } catch (error) {
                    console.error("Error playing next clip:", error);
                }
            } else {
                // No more clips in the queue
                setIsPlaying(false);
                setCurrentlyPlayingClip(null);
            }
        }
    };

    const pauseClip = () => {
        if (isPlayingRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
            posthog.capture('clip_paused', {
                clip: audioRef.current.id,
                clip_title: audioRef.current.title,
            })
        }
    };

    const resumeClip = () => {
        if (!isPlayingRef.current && currentlyPlayingClipRef.current) {
            audioRef.current.play();
            setIsPlaying(true);
            posthog.capture('clip_resumed', {
                clip: audioRef.current.id,
                clip_title: audioRef.current.title,
            })
        }
    };

    const addToQueue = (clip) => {
        if (!queueRef.current.some(c => c.id === clip.id)) {
            removeFromAutoqueueById(clip.id);
            setQueue((queue) => [...queue, clip]);
            posthog.capture('clip_added_to_queue', {
                clip: clip.id,
                clip_title: clip.title,
            })
        }
    };

    const addToQueueAtIndex = (clip, index) => {
        if (!queueRef.current.some(c => c.id === clip.id)) {
            const updatedQueue = [...queueRef.current];
            updatedQueue.splice(index, 0, clip);
            setQueue(updatedQueue);
            posthog.capture('clip_added_to_queue', {
                clip: clip.id,
                clip_title: clip.title,
            })
        } else {
            moveClipInQueue(queueRef.current.findIndex(c => c.id === clip.id), index);
        }
    };

    const addToAutoqueue = (clip) => {
        if (!autoqueueRef.current.some(c => c.id === clip.id)) {
            setAutoqueue((autoqueue) => [...autoqueue, clip]);
            posthog.capture('clip_added_to_autoqueue', {
                clip: clip.id,
                clip_title: clip.title,
            })
        }
    };

    const addToAutoqueueAtIndex = (clip, index) => {
        if (!autoqueueRef.current.some(c => c.id === clip.id)) {
            const updatedAutoqueue = [...autoqueueRef.current];
            updatedAutoqueue.splice(index, 0, clip);
            setAutoqueue(updatedAutoqueue);
            posthog.capture('clip_added_to_autoqueue', {
                clip: clip.id,
                clip_title: clip.title,
            })
        } else {
            moveClipInAutoqueue(autoqueueRef.current.findIndex(c => c.id === clip.id), index);
        }
    };

    const removeFromQueue = (index) => {
        const updatedQueue = queueRef.current.filter((_, i) => i !== index);
        posthog.capture('clip_removed_from_queue')
        setQueue(updatedQueue);
    };

    const removeFromAutoqueue = (index) => {
        const updatedAutoqueue = autoqueueRef.current.filter((_, i) => i !== index);
        posthog.capture('clip_removed_from_autoqueue')
        setAutoqueue(updatedAutoqueue);
    };

    const removeFromQueueById = (id) => {
        const updatedQueue = queueRef.current.filter(clip => clip.id !== id);
        posthog.capture('clip_removed_from_queue', {
            clip_id: id
        })
        setQueue(updatedQueue);
    };

    const removeFromAutoqueueById = (id) => {
        const updatedAutoqueue = autoqueueRef.current.filter(clip => clip.id !== id);
        posthog.capture('clip_removed_from_autoqueue', {
            clip_id: id
        })
        setAutoqueue(updatedAutoqueue);
    }

    const moveClipInQueue = (fromIndex, toIndex) => {
        if (toIndex < 0 || toIndex >= queueRef.current.length) {
            return;
        }
        const newQueue = [...queueRef.current];
        const [removedClip] = newQueue.splice(fromIndex, 1); // Remove clip from its original position
        newQueue.splice(toIndex, 0, removedClip); // Insert clip at the new position
        posthog.capture('clip_moved_in_queue', {
            clip: removedClip.id,
            clip_title: removedClip.title,
        })
        setQueue(newQueue);
    };

    const moveClipInAutoqueue = (fromIndex, toIndex) => {
        if (toIndex < 0 || toIndex >= autoqueueRef.current.length) {
            return;
        }
        const newAutoqueue = [...autoqueueRef.current];
        const [removedClip] = newAutoqueue.splice(fromIndex, 1); // Remove clip from its original position
        newAutoqueue.splice(toIndex, 0, removedClip); // Insert clip at the new position
        posthog.capture('clip_moved_in_autoqueue', {
            clip: removedClip.id,
            clip_title: removedClip.title,
        })
        setAutoqueue(newAutoqueue);
    }

    const setVolume = (volume) => {
        if (volume >= 0 && volume <= 1) {
            audioRef.current.volume = volume;
        }
    };

    const setPlaybackRate = (rate) => {
        if (rate > 0) { // Ensure the rate is a positive number
            audioRef.current.playbackRate = rate;
            setPlaybackRateState(rate); // Update state to reflect the new playback rate
            posthog.capture('playback_rate_changed', {
                new_playback_rate: rate
            })
        }
    };

    return (
        <AudioContext.Provider value={{ playClip, playNextClip, pauseClip, resumeClip, addToQueue, addToQueueAtIndex, addToAutoqueue, addToAutoqueueAtIndex, removeFromQueue, removeFromAutoqueue, removeFromQueueById, removeFromAutoqueueById, moveClipInQueue, moveClipInAutoqueue, setVolume, setPlaybackRate, playbackRate, isPlaying, currentlyPlayingClip, queue, autoqueue, audioRef }}>
            {children}
        </AudioContext.Provider>
    );
};